import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Avatar,
  Stack,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
} from "@mui/material";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import LowPriorityIcon from "@mui/icons-material/LowPriority";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BusinessIcon from "@mui/icons-material/Business";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { useDispatch, useSelector } from "react-redux";
import { DensityMedium } from "@mui/icons-material";
import TaskDetailDialog from "./TaskDetailDialog";
import { getTaskCategories, getTaskPriorities, getTasks } from "../../redux/features/taskManagementSlice";
import { getUserUnder } from "../../redux/features/generalSlice";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// ------------------ CONFIG MAPS ------------------
const priorityConfig = {
  1: {
    label: "Critical",
    icon: <DragHandleIcon sx={{ fontSize: 14 }} />,
    color: "#1D4ED8",
    bg: "#EFF6FF",
    border: "#93C5FD",
  },
  2: {
    label: "High",
    icon: <PriorityHighIcon sx={{ fontSize: 14 }} />,
    color: "#B91C1C",
    bg: "#FEF2F2",
    border: "#FCA5A5",
  },
  3: {
    label: "Medium",
    icon: <DensityMedium sx={{ fontSize: 14 }} />,
    color: "#D97706",
    bg: "#FFFBEB",
    border: "#FCD34D",
  },
  4: {
    label: "Low",
    icon: <LowPriorityIcon sx={{ fontSize: 14 }} />,
    color: "#15803D",
    bg: "#F0FDF4",
    border: "#86EFAC",
  },
};

const assignmentStatusConfig = {
  0: {
    label: "Pending",
    icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} />,
    color: "#C2410C",
    bg: "#FFF7ED",
    border: "#FDBA74",
  },
  1: {
    label: "Assigned",
    icon: <AssignmentIndIcon sx={{ fontSize: 14 }} />,
    color: "#6D28D9",
    bg: "#F5F3FF",
    border: "#C4B5FD",
  },
  2: {
    label: "In Progress",
    icon: <AutorenewIcon sx={{ fontSize: 14 }} />,
    color: "#1D4ED8",
    bg: "#EFF6FF",
    border: "#93C5FD",
  },
  3: {
    label: "On Hold",
    icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
    color: "#92400E",
    bg: "#FFFBEB",
    border: "#FCD34D",
  },
  4: {
    label: "Rejected",
    icon: <CancelIcon sx={{ fontSize: 14 }} />,
    color: "#B91C1C",
    bg: "#FEF2F2",
    border: "#FCA5A5",
  },
  5: {
    label: "Completed",
    icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
    color: "#15803D",
    bg: "#F0FDF4",
    border: "#86EFAC",
  },
};

// Avatar gradients (cycles through)
const avatarGradients = [
  "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
  "linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)",
  "linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)",
  "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
  "linear-gradient(135deg, #f59e0b 0%, #fb923c 100%)",
];

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// ------------------ COMPONENT ------------------
const AllDepartmentTasks = ({  }) => {
  const { taskdata, taskCategories, priorities } = useSelector((state) => state.taskManagement);
  const { dataUserUnder } = useSelector((state) => state.general);

  const data = taskdata || [];

  // fallback categories
  const defaultCategories = [
    { task_category_id: 1, task_category_name: "Image" },
    { task_category_id: 2, task_category_name: "Poster" },
    { task_category_id: 3, task_category_name: "Banner" },
    { task_category_id: 4, task_category_name: "Vedio" },
  ];

  const categories = taskCategories && taskCategories.length ? taskCategories : defaultCategories;

  // users for employee filter
  const users = Array.isArray(dataUserUnder) ? dataUserUnder : [];

  // priorities options fallback
  const defaultPriorities = [
    { approval_priority_id: 1, approval_priority_name: "Critical" },
    { approval_priority_id: 2, approval_priority_name: "High" },
    { approval_priority_id: 3, approval_priority_name: "Medium" },
    { approval_priority_id: 4, approval_priority_name: "Low" },
  ];
  const priorityOptions = priorities && priorities.length ? priorities : defaultPriorities;

  const [categoryFilter, setCategoryFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState(null); // will store selected user object
  const [priorityFilter, setPriorityFilter] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [date, setDate] = useState(dayjs());

  const handleView = (task) => {
    setSelectedTask(task);
    setOpenDialog(true);
  };

  const filteredData = data.filter((t) => {
    // category filter
    if (categoryFilter && String(t.task_category_id) !== String(categoryFilter)) return false;
    // employee filter - compare with task_assigned_to (expecting userID)
    if (employeeFilter && employeeFilter.userID && String(t.task_assigned_to) !== String(employeeFilter.userID)) return false;
    // priority filter - compare with task.priority (approval_priority_id)
    if (priorityFilter && String(t.priority) !== String(priorityFilter)) return false;
    return true;
  });

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTaskCategories());
    dispatch(getTasks({ status: 'all' }));
    dispatch(getUserUnder());
    dispatch(getTaskPriorities())
  }, [dispatch, date]);
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, background: "#FFFFFF" }}>
      {/* Section Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(99, 102, 241, 0.25)" }}>
            <ListAltIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1E293B", letterSpacing: "-0.3px", lineHeight: 1.2 }}>
              All Department Tasks
            </Typography>
            <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: "0.78rem" }}>
              {filteredData?.length || 0} task{filteredData?.length !== 1 ? "s" : ""} shown
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                views={['year', 'month']}
                value={date}
                onChange={(newValue) => setDate(newValue)}
                format="MMM YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)} sx={{ borderRadius: 2 }}>
              <MenuItem value="">All</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.task_category_id} value={cat.task_category_id}>
                  {cat.task_category_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            size="small"
            options={users}
            getOptionLabel={(u) => u?.name || u?.fullName || u?.userName || ''}
            value={employeeFilter}
            onChange={(e, newVal) => setEmployeeFilter(newVal)}
            sx={{ minWidth: 220 }}
            renderInput={(params) => <TextField {...params} label="Employee" />}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={priorityFilter} label="Priority" onChange={(e) => setPriorityFilter(e.target.value)} sx={{ borderRadius: 2 }}>
              <MenuItem value="">All</MenuItem>
              {priorityOptions.map((p) => (
                <MenuItem key={p.approval_priority_id} value={p.approval_priority_id}>
                  {p.approval_priority_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Chip label={`Total: ${data?.length || 0}`} size="small" sx={{ background: "linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)", color: "#6366F1", fontWeight: 700, fontSize: "0.75rem", border: "1px solid #E0E7FF", height: 28 }} />
        </Stack>
      </Stack>

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: "1px solid #E2E8F0", overflow: "auto", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", "&::-webkit-scrollbar": { height: 8, width: 8 }, "&::-webkit-scrollbar-track": { background: "#F1F5F9" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: 4, "&:hover": { background: "#94A3B8" } } }}>
        <Table sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow sx={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)", "& th": { color: "#FFFFFF", fontWeight: 700, fontSize: "0.78rem", letterSpacing: "0.5px", textTransform: "uppercase", borderBottom: "none", py: 2, whiteSpace: "nowrap" } }}>
              <TableCell sx={{ pl: 3, width: 60 }}>#</TableCell>
              <TableCell sx={{ minWidth: 280 }}>Task Title</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Target Department</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Assigned To</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Assigned By</TableCell>
              <TableCell sx={{ minWidth: 130 }}>Priority</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Due Date</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Created On</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Status</TableCell>
              <TableCell sx={{ pr: 3, width: 80, textAlign: "center" }}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!filteredData || filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ py: 8, textAlign: "center" }}>
                  <ListAltIcon sx={{ fontSize: 48, color: "#CBD5E1", mb: 1 }} />
                  <Typography variant="body2" sx={{ color: "#94A3B8", fontWeight: 600 }}>
                    No tasks available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((task, index) => {
                const priority = priorityConfig[task.priority] || priorityConfig[1];
                const status = assignmentStatusConfig[task.assignment_status] || assignmentStatusConfig[0];
                const isOverdue = new Date(task.expected_delivery_date) < new Date() && task.assignment_status !== 5;
                const assigneeGradient = avatarGradients[index % avatarGradients.length];

                return (
                  <TableRow key={task.task_id} sx={{ transition: "all 0.2s ease", "&:hover": { background: "#F8FAFC" }, "& td": { borderBottom: "1px solid #F1F5F9", py: 2 }, "&:last-child td": { borderBottom: "none" } }}>
                    <TableCell sx={{ pl: 3 }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: 2, background: "linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "0.78rem", color: "#6366F1" }}>
                        {index + 1}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ fontWeight: 600, color: "#1E293B", fontSize: "0.86rem", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {task.title}
                        </Typography>
                        {isOverdue && (
                          <Tooltip title="Overdue" arrow>
                            <ErrorOutlineIcon sx={{ fontSize: 16, color: "#EF4444" }} />
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: "0.7rem", display: "block", mt: 0.3 }}>
                        ID: #{task.task_id}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 28, height: 28, borderRadius: 1.5, background: "#F5F3FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <BusinessIcon sx={{ fontSize: 16, color: "#8B5CF6" }} />
                        </Box>
                        <Typography sx={{ fontSize: "0.83rem", color: "#334155", fontWeight: 500 }}>
                          {task.target_dept_name || "—"}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      {task.assigned_to_name ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar sx={{ width: 30, height: 30, background: assigneeGradient, fontSize: "0.72rem", fontWeight: 700, boxShadow: "0 2px 6px rgba(0,0,0,0.08)" }}>
                            {getInitials(task.assigned_to_name || "—")}
                          </Avatar>
                          <Typography sx={{ fontSize: "0.83rem", color: "#334155", fontWeight: 500 }}>{task.assigned_to_name}</Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={0.8}>
                          <PersonOutlineIcon sx={{ fontSize: 18, color: "#CBD5E1" }} />
                          <Typography sx={{ fontSize: "0.8rem", color: "#94A3B8", fontStyle: "italic" }}>Unassigned</Typography>
                        </Stack>
                      )}
                    </TableCell>

                    <TableCell>
                      {task.created_by_name ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar sx={{ width: 30, height: 30, background: "linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)", fontSize: "0.72rem", fontWeight: 700, boxShadow: "0 2px 6px rgba(0,0,0,0.08)" }}>
                            {getInitials(task.assigned_by_name || "—")}
                          </Avatar>
                          <Typography sx={{ fontSize: "0.83rem", color: "#334155", fontWeight: 500 }}>{task.assigned_by_name}</Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={0.8}>
                          <PersonOutlineIcon sx={{ fontSize: 18, color: "#CBD5E1" }} />
                          <Typography sx={{ fontSize: "0.8rem", color: "#94A3B8", fontStyle: "italic" }}>Unassigned</Typography>
                        </Stack>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip icon={React.cloneElement(priority.icon, { sx: { fontSize: 14, color: `${priority.color} !important` } })} label={priority.label} size="small" sx={{ height: 26, background: priority.bg, color: priority.color, border: `1px solid ${priority.border}`, fontWeight: 600, fontSize: "0.72rem", borderRadius: "6px", "& .MuiChip-icon": { color: `${priority.color} !important`, ml: "6px" }, "& .MuiChip-label": { px: 0.9 } } } />
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.8}>
                        <CalendarTodayIcon sx={{ fontSize: 14, color: isOverdue ? "#EF4444" : "#64748B" }} />
                        <Typography sx={{ fontSize: "0.82rem", color: isOverdue ? "#EF4444" : "#334155", fontWeight: isOverdue ? 600 : 500 }}>{formatDate(task.expected_delivery_date)}</Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.8}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: "#64748B" }} />
                        <Typography sx={{ fontSize: "0.82rem", color: "#334155", fontWeight: 500 }}>{formatDate(task.created_at)}</Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Chip icon={React.cloneElement(status.icon, { sx: { fontSize: 14, color: `${status.color} !important` } })} label={task.assignment_status_name || status.label} size="small" sx={{ height: 26, background: status.bg, color: status.color, border: `1px solid ${status.border}`, fontWeight: 600, fontSize: "0.72rem", borderRadius: "6px", "& .MuiChip-icon": { color: `${status.color} !important`, ml: "6px" }, "& .MuiChip-label": { px: 0.9 } }} />
                    </TableCell>

                    <TableCell sx={{ pr: 3, textAlign: "center" }}>
                      <Tooltip title="View Details" arrow>
                        <IconButton size="small" onClick={() => handleView(task)} sx={{ width: 34, height: 34, background: "linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)", color: "#6366F1", transition: "all 0.2s ease", "&:hover": { background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", color: "#FFFFFF", transform: "scale(1.05)" } }}>
                          <VisibilityIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {openDialog && selectedTask && (
        <TaskDetailDialog open={openDialog} onClose={() => setOpenDialog(false)} task={selectedTask} filters={{ status: 'all' }} />
      )}
    </Box>
  );
};

export default AllDepartmentTasks;
